/**
 * Trading Terminal — API Client
 * Phase 2: Replaces mock.js with real FastAPI backend calls.
 *
 * Base URL: reads from REACT_APP_API_URL env var, falls back to localhost:8000.
 * All requests are GET unless noted. Errors return { error, stale: true, data: lastCached }.
 */

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Simple in-memory stale cache
const _cache = {};

/**
 * Core fetch wrapper.
 * On error, returns last cached value with { _stale: true } flag.
 */
export async function apiFetch(path, options = {}) {
  const url = `${BASE_URL}${path}`;
  try {
    const res = await fetch(url, {
      credentials: "include",            // send httpOnly session cookie on every request
      headers: { "Content-Type": "application/json" },
      ...options,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    _cache[path] = data;
    return data;
  } catch (err) {
    console.warn(`[API] ${path} failed:`, err.message);
    if (_cache[path]) {
      return { ..._cache[path], _stale: true, _error: err.message };
    }
    return { _error: err.message, _stale: false };
  }
}

export async function apiPost(path, body = {}) {
  return apiFetch(path, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ── Typed endpoint helpers ──────────────────────────────────────────────────

/** Index quotes: NIFTY 50, Bank Nifty, Sensex, VIX, sectors */
export const getIndices = () => apiFetch("/api/prices/indices");

/** Commodity + FX quotes (MCX proxied from US futures) */
export const getCommodities = () => apiFetch("/api/prices/commodities");

/** All ~180 F&O stocks daily OHLCV */
export const getFoStocks = () => apiFetch("/api/prices/fo-stocks");

/** Sparkline — last N session closes for a symbol */
export const getSparkline = (symbol, sessions = 10) =>
  apiFetch(`/api/prices/sparkline/${encodeURIComponent(symbol)}?sessions=${sessions}`);

/** NSE daily: FII/DII, participant OI, build-up summary */
export const getNseDaily = () => apiFetch("/api/nse/daily");

/** Lot sizes: { SYMBOL: lot_size } */
export const getLotSizes = () => apiFetch("/api/nse/lot-sizes");

/** Top futures by OI with build-up classification */
export const getBhavcopy = (top = 50) => apiFetch(`/api/nse/bhavcopy?top=${top}`);

/** 14 asset class RS vs NIFTY */
export const getAssetRS = () => apiFetch("/api/rs/assets");

/** All ~180 F&O stock RS vs NIFTY */
export const getFoStockRS = () => apiFetch("/api/rs/fo-stocks");

/** Single asset RS with full chart series */
export const getSingleAssetRS = (id) => apiFetch(`/api/rs/asset/${id}`);

/**
 * NSE index / asset quote + period performance + daily sessions.
 * Accepts: index tickers (NIFTYIT, NIFTY50, BANKNIFTY),
 *          RS labels (NIFTY IT, Bank Nifty, Gold, Fin Services),
 *          RS asset IDs (gold, crude, natgas, usdinr).
 * Returns: { ltp, open, high, low, prev_close, change, change_pct,
 *            period_returns: [{label, v}], daily_sessions: [{label, v}] }
 */
export const getIndexQuote = (symbol) =>
  apiFetch(`/api/prices/index/${encodeURIComponent(symbol)}`);

/**
 * Sector RRG — RS-Ratio (x) and RS-Momentum (y) with daily/weekly trail.
 *
 * algo="jdk"  (default) — JdK dual-EMA; uses normWeeks (13|26|52).
 * algo="wma"            — Pinescript WMA; uses length (5–50, default 20).
 *   testStocks=true     — plot the 10 Pinescript validation stocks instead of sectors.
 */
export const getRRGData = (
  timeframe  = "daily",
  normWeeks  = 52,
  algo       = "jdk",
  length     = 20,
  testStocks = false,
) => {
  const params = new URLSearchParams({ timeframe, norm_weeks: normWeeks, algo });
  if (algo === "wma") {
    params.set("length", length);
    if (testStocks) params.set("test_stocks", "true");
  }
  return apiFetch(`/api/rs/rrg?${params}`);
};

/** All F&O stocks WMA RRG benchmarked vs NIFTY 50 (cross-sector RS comparison) */
export const getStockNiftyRRGData = (timeframe = "weekly", length = 20) =>
  apiFetch(`/api/rs/rrg/stocks/nifty?timeframe=${timeframe}&length=${length}`);

/** Stock-level RRG — stocks in given sectors benchmarked vs their sector index */
export const getStockRRGData = (
  sectors   = [],          // array of SECTOR_TO_INDEX keys, e.g. ['IT', 'Banking']
  timeframe = "weekly",
  algo      = "wma",
  length    = 20,
) => {
  if (!sectors.length) return Promise.resolve({ stocks: [], computed_at: null });
  const params = new URLSearchParams({
    sectors:   sectors.join(","),
    timeframe,
    algo,
    length,
  });
  return apiFetch(`/api/rs/rrg/stocks?${params}`);
};

/** News feed — all sources, last 7 days */
export const getNews = (days = 7, category = null) => {
  const params = new URLSearchParams({ days });
  if (category) params.set("category", category);
  return apiFetch(`/api/news/feed?${params}`);
};

/** EIA storage: next report countdown + last 10 weeks */
export const getEiaCountdown = () => apiFetch("/api/eia/countdown");

/** EIA stored forecasts: { forecasts: [{week_date, forecast_bcf}] } */
export const getEiaForecasts = () => apiFetch("/api/eia/forecasts");

/** Save a manual EIA forecast: { week_date, forecast_bcf } */
export const saveEiaForecast = (week_date, forecast_bcf) =>
  apiPost("/api/eia/forecast", { week_date, forecast_bcf });

/** Delete a manual EIA forecast by week_date */
export const deleteEiaForecast = (week_date) =>
  apiFetch(`/api/eia/forecast/${week_date}`, { method: "DELETE" });

/** Price DB status: data_as_of date, symbol_count, rs_computed_at */
export const getDataStatus = () => apiFetch("/api/rs/data/status");

/** Trigger delta price sync + RS recompute in background (legacy combined action) */
export const syncPriceData = () => apiPost("/api/rs/data/sync");

/** Sync EOD price data only — no RS recompute (owner-only) */
export const syncEodData = () => apiPost("/api/rs/data/sync-eod");

/** Recompute RS/RRG/breadth from existing price data — no price fetch (owner-only) */
export const recomputeRS = () => apiPost("/api/rs/data/recompute");

/** Top-down RS: stock RS vs sector index for each mapped sector */
export const getTopdownRS = () => apiFetch("/api/rs/topdown");

/** Market breadth: advances/declines */
export const getBreadth = (live = false) =>
  apiFetch(`/api/breadth${live ? "?live=true" : ""}`);

/** Last N days of NIFTY50 breadth history from nifty_daily (for Dashboard bars) */
export const getNiftyBreadthHistory = (days = 10) =>
  apiFetch(`/api/nifty/breadth-history?days=${days}`);

/** Intraday NIFTY50 breadth snapshots (5-min resolution).
 *  Returns { date, snapshots: [{time, adv, dec, total, adv_pct}] }
 *  Omit date for today's data. */
export const getBreadthIntraday = (date = null) =>
  apiFetch(`/api/breadth/intraday${date ? `?date=${date}` : ""}`);

/** 52-week highs and lows for F&O stocks */
export const getExtremes = () => apiFetch("/api/extremes");

/** Trigger manual full refresh (returns immediately, runs in background) */
export const triggerRefresh = () => apiPost("/api/refresh");

/** Health check */
export const getHealth = () => apiFetch("/api/health");

// ── Options Chain (Phase 5) ─────────────────────────────────────────────────

/** All supported option underlyings: { nse: [...], mcx: [...] } */
export const getOptionInstruments = () => apiFetch("/api/options/instruments");

/** Dynamic symbol list from kite_instruments: { indices, stocks, mcx, source } */
export const getOptionSymbols = () => apiFetch("/api/options/symbols");

/** Available expiry dates: { symbol, exchange, expiries: ['YYYY-MM-DD', ...] } */
export const getOptionExpiries = (symbol, exchange = "NFO") =>
  apiFetch(`/api/options/expiries?symbol=${encodeURIComponent(symbol)}&exchange=${exchange}`);

/** Full option chain with Greeks: { meta: {...}, rows: [...] } */
export const getOptionChain = (symbol, expiry, exchange = "NFO") =>
  apiFetch(`/api/options/chain?symbol=${encodeURIComponent(symbol)}&expiry=${encodeURIComponent(expiry)}&exchange=${exchange}`);

/**
 * Batch LTP + IV for specific strategy legs — single kite.quote() call on the backend.
 * legs: [{symbol, exchange, expiry, strike, type}]
 * Returns { results: [{ltp, iv}] } in the same order as input.
 */
export const getLegLtpBatch = (legs) =>
  apiFetch("/api/options/ltp-batch", { method: "POST", body: JSON.stringify({ legs }) });

/** Phase 2 — intraday PCR history: { history: [{time,pcr,total_ce,total_pe,spot}] } */
export const getOIPCRHistory = (symbol, expiry, exchange = "NFO") =>
  apiFetch(`/api/options/pcr-history?symbol=${encodeURIComponent(symbol)}&expiry=${encodeURIComponent(expiry)}&exchange=${exchange}`);

/** Today's 1-min NIFTY/BANKNIFTY spot candles from Kite: { points: [{time,close}] } */
export const getNiftyIntraday = (symbol = "NIFTY") =>
  apiFetch(`/api/options/nifty-intraday?symbol=${encodeURIComponent(symbol)}`);

/** Phase 2 — per-strike OI snapshots at 6 time offsets for historical PCR/CPR lines */
export const getOIStrikeHistory = (symbol, expiry, exchange = "NFO") =>
  apiFetch(`/api/options/oi-strike-history?symbol=${encodeURIComponent(symbol)}&expiry=${encodeURIComponent(expiry)}&exchange=${exchange}`);

/** Full intraday per-strike OI timeline (all ~5-min snaps) for the time range slider */
export const getOIStrikeTimeline = (symbol, expiry, exchange = "NFO") =>
  apiFetch(`/api/options/oi-strike-timeline?symbol=${encodeURIComponent(symbol)}&expiry=${encodeURIComponent(expiry)}&exchange=${exchange}`);

/** Historical IV (VIX proxy), HV10, HV30, IV-RV spread, IV percentile — ~1 year of daily data */
export const getIVHistory = (days = 252) =>
  apiFetch(`/api/options/iv-history?days=${days}`);

/** Max Pain history: 5-min time-series for last N days, all expiries (continuous across rolls) */
export const getMaxPainHistory = (symbol, exchange = "NFO", days = 5) =>
  apiFetch(`/api/options/max-pain-history?symbol=${encodeURIComponent(symbol)}&exchange=${exchange}&days=${days}`);

/**
 * SPAN + Exposure margin for a multi-leg strategy via Kite basket_order_margins.
 * legs: [{ symbol, exchange, expiry, strike, opt_type, action, lots, lot_size }]
 * Returns { initial, span, exposure, final, orders_count, missing, error }
 */
export const getStrategyMargin = (legs, defaultLotSize = 1) =>
  apiFetch("/api/options/strategy-margin", {
    method: "POST",
    body: JSON.stringify({ legs, default_lot_size: defaultLotSize }),
  });

// ── Strategy Builder persistence ────────────────────────────────────────────

/** Load saved options strategies for a symbol (Options Builder): { symbol, strategies: [...], updated_at } */
export const loadStrategiesAPI = (symbol) =>
  apiFetch(`/api/options/strategies/${encodeURIComponent(symbol)}`);

/** Save options strategies array for a symbol (Options Builder upsert). */
export const saveStrategiesAPI = (symbol, strategies) =>
  apiFetch(`/api/options/strategies/${encodeURIComponent(symbol)}`, {
    method: "PUT",
    body: JSON.stringify({ strategies }),
  });

/** List all enabled strategies in the registry: { strategies: [...] } */
export const listStrategyRegistry = () =>
  apiFetch("/api/strategies");

/** Get a single strategy from the registry by slug ID. */
export const getStrategyById = (id) =>
  apiFetch(`/api/strategies/${encodeURIComponent(id)}`);

// ── NIFTY History (NiftyTab) ────────────────────────────────────────────────

/**
 * Rolling NIFTY daily history.
 * Returns { rows: [...], count, days }
 * Row fields match NIFTY_DATA mock: open, high, low, close, chgPct, vix,
 * futOI, oiChgPct, buildUp, isExpiry, advDec, pcrNear, fii, dii, etc.
 */
export const getNiftyHistory = (days = 90) =>
  apiFetch(`/api/nifty/history?days=${days}`);

/**
 * Weekly NIFTY table (expiry-aligned, Jan 2024+).
 * Returns { weeks: [...], count }
 */
export const getNiftyWeekly = () => apiFetch("/api/nifty/weekly");

/** Monthly NIFTY aggregate table (Jan 2024+). Returns { months: [...], count } */
export const getNiftyMonthly = () => apiFetch("/api/nifty/monthly");

/** NIFTY diagnostics: Factor Agreement + overall prediction stats. window = 30|60|90 */
export const getNiftyDiagnostics = (window = 60) =>
  apiFetch(`/api/nifty/diagnostics?window=${window}`);

/** Log a structured bucket correction. */
export const postNiftyCorrection = (payload) =>
  apiPost("/api/nifty/corrections", payload);

/**
 * Trigger NSE archive backfill in the background.
 * Returns { started, message } or { started: false, reason: 'already_running' }
 */
export const triggerNiftyBackfill = () => apiPost("/api/nifty/backfill");

/**
 * Force-refresh all NIFTY OHLCV + VIX from Kite for Jan 2024 → today.
 * Run ONCE to correct historical rows that captured intraday closes.
 * Returns { success, rows_updated } or { success: false, error }.
 */
export const refreshNiftyOHLCV = () => apiPost("/api/nifty/refresh-ohlcv");

/** Check backfill progress: { running, done, total, errors, last_run } */
export const getNiftyBackfillStatus = () => apiFetch("/api/nifty/backfill/status");

/**
 * Kick off equity FII/DII backfill for the last `days` trading days.
 * Fills NULL fii/dii rows by re-downloading from NSE historical API.
 */
export const triggerEquityFiiBackfill = (days = 30) =>
  apiPost(`/api/nifty/backfill-equity-fii?days=${days}`);

/** Poll equity FII/DII backfill progress. */
export const getEquityFiiBackfillStatus = () =>
  apiFetch("/api/nifty/backfill-equity-fii/status");

// ── Kite Connect ────────────────────────────────────────────────────────────

/** Kite connection status */
export const getKiteStatus = () => apiFetch("/kite/status");

/** GIFT Nifty real-time quote (Kite only) */
export const getGiftNifty = () => apiFetch("/api/prices/gift-nifty");

// ── Tab 2 Performance ───────────────────────────────────────────────────────

/**
 * Multi-timeframe performance table for a given universe.
 * Returns: { universe, cached, rows: [{symbol, label, sector, closes: [{date, close}]}] }
 *
 * @param {'indices'|'nifty100'|'fo'} universe
 * @param {boolean} force  bypass server-side cache
 */
export const getPerformanceTable = (universe = "indices", force = false) =>
  apiFetch(`/api/performance/table?universe=${universe}${force ? "&force=true" : ""}`);

/** 52-week high and low for any NSE stock symbol: { high_52w, low_52w } */
export const get52W = (symbol) =>
  apiFetch(`/api/prices/52w/${encodeURIComponent(symbol)}`);

// ── NSE Directory ────────────────────────────────────────────────────────────

/**
 * All ~750 NIFTY Total Market stocks with index membership flags.
 * Returns: { stocks: [...], count, synced_at }
 * Each stock: { symbol, company_name, sector_nse, sector_ours, is_fo, memberships: [index_id...] }
 */
export const getDirectoryStocks = () => apiFetch("/api/directory/stocks");

/**
 * All index metadata (id, label, category, stock_count, last_synced).
 * Returns: { indices: [...] }
 */
export const getDirectoryIndices = () => apiFetch("/api/directory/indices");

/**
 * Constituent stocks for one specific index, with weightage.
 * Returns: { id, label, category, is_synthetic, stock_count, stocks: [...] }
 * Each stock: { symbol, company_name, sector_nse, sector_ours, weightage, rank, is_fo }
 */
export const getDirectoryIndex = (indexId) =>
  apiFetch(`/api/directory/index/${encodeURIComponent(indexId)}`);

/**
 * Trigger a full NSE Directory sync in the background.
 * Downloads ~40 index constituent CSVs from NSE archives.
 * Takes ~30–60 seconds. Poll getDirectoryStocks() to check when data appears.
 */
export const syncDirectory = () => apiPost("/api/directory/sync");

// ── Strategy 4: Weekly NIFTY AI Prediction ────────────────────────────────
/** Generate (or return cached) prediction for the current week. Set force=true to regenerate. */
export const generateStrategy4Prediction = (force = false) =>
  apiPost(`/api/strategy4/predict${force ? "?force=true" : ""}`);

/** Current week prediction + outcome (no generation triggered). */
export const getStrategy4Current = () => apiFetch("/api/strategy4/current");

/** Last N weeks of predictions + outcomes. */
export const getStrategy4History = (weeks = 52) =>
  apiFetch(`/api/strategy4/history?weeks=${weeks}`);

/** Record actual outcome after Tuesday expiry. */
export const postStrategy4Outcome = (payload) =>
  apiPost("/api/strategy4/outcome", payload);

/** Raw signal snapshot for current week (debug / manual review). */
export const getStrategy4Signals = () => apiFetch("/api/strategy4/signals");

/** Upcoming macro events (next N days). */
export const getStrategy4Events = (daysAhead = 30) =>
  apiFetch(`/api/strategy4/events?days_ahead=${daysAhead}`);

/** Full events calendar with optional filters for the Events Calendar sub-tab. */
export const getAllStrategy4Events = ({ fromDate, toDate, region, category, impact } = {}) => {
  const p = new URLSearchParams();
  if (fromDate)  p.set("from_date", fromDate);
  if (toDate)    p.set("to_date",   toDate);
  if (region)    p.set("region",    region);
  if (category)  p.set("category",  category);
  if (impact)    p.set("impact",    impact);
  const qs = p.toString();
  return apiFetch(`/api/strategy4/events/all${qs ? `?${qs}` : ""}`);
};

/** Manually add a macro event. */
export const addStrategy4Event = (payload) =>
  apiPost("/api/strategy4/events", payload);

/** Edit an existing event (PATCH). */
export const updateStrategy4Event = (eventId, payload) =>
  apiFetch(`/api/strategy4/events/${eventId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });

/** Delete an event. */
export const deleteStrategy4Event = (eventId) =>
  apiFetch(`/api/strategy4/events/${eventId}`, { method: "DELETE" });

/** Refresh US events from FMP free API. */
export const refreshStrategy4EventsFMP = (apiKeyOverride = null) => {
  const qs = apiKeyOverride ? `?api_key_override=${encodeURIComponent(apiKeyOverride)}` : "";
  return apiPost(`/api/strategy4/events/refresh-fmp${qs}`);
};

// Actions CRUD (record_type='action' rows in economic_events)
export const addAction = (payload) =>
  apiPost("/api/strategy4/actions", payload);

export const updateAction = (id, payload) =>
  apiFetch(`/api/strategy4/actions/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const deleteAction = (id) =>
  apiFetch(`/api/strategy4/actions/${id}`, { method: "DELETE" });

export const completeCalendarItem = (id) =>
  apiPost(`/api/strategy4/events/${id}/complete`);

export const reopenCalendarItem = (id) =>
  apiPost(`/api/strategy4/events/${id}/reopen`);

// ── Market Movers ────────────────────────────────────────────────────────────

/** Live contribution table for NIFTY50 / SENSEX / BANKNIFTY (Kite real-time). */
export const getMarketMoversLive = (indexId) =>
  apiFetch(`/api/market-movers/live/${encodeURIComponent(indexId)}`);

/** EOD contribution table for a historical date. */
export const getMarketMoversHistory = (indexId, date) =>
  apiFetch(`/api/market-movers/history/${encodeURIComponent(indexId)}${date ? `?date=${date}` : ''}`);

/** Available EOD dates for a given index (last N trading days with data). */
export const getMarketMoversDates = (indexId, n = 5) =>
  apiFetch(`/api/market-movers/dates/${encodeURIComponent(indexId)}?n=${n}`);

/** All available EOD dates across all indices (union). */
export const getMarketMoversAllDates = (n = 5) =>
  apiFetch(`/api/market-movers/all-dates?n=${n}`);

/** Admin: get constituents for an index. */
export const getMarketMoversConstituents = (indexId) =>
  apiFetch(`/api/market-movers/constituents/${encodeURIComponent(indexId)}`);

/** Admin: update a constituent's company name and weight. */
export const updateMarketMoverConstituent = (indexId, symbol, company_name, weightage) =>
  apiFetch(`/api/market-movers/constituents/${encodeURIComponent(indexId)}/${encodeURIComponent(symbol)}`, {
    method: 'PUT',
    body: JSON.stringify({ company_name, weightage }),
  });

/** Admin: upsert a constituent row. */
export const upsertMarketMoverConstituent = (indexId, row) =>
  apiFetch(`/api/market-movers/constituents/${encodeURIComponent(indexId)}`, {
    method: 'POST',
    body: JSON.stringify(row),
  });

/** Admin: re-seed constituent data from hardcoded May 2026 weights. */
export const seedMarketMoversConstituents = (force = false) =>
  apiFetch(`/api/market-movers/seed?force=${force}`, { method: 'POST' });

/** Bulk upload events CSV (multipart form). */
export const uploadStrategy4EventsCSV = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const url = `${BASE_URL}/api/strategy4/events/upload-csv`;
  try {
    const res = await fetch(url, { method: "POST", body: form });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  } catch (err) {
    console.warn("[API] events/upload-csv failed:", err.message);
    return { _error: err.message };
  }
};
